import React, { useEffect, useRef, useState } from "react";
import { Mic, X } from "lucide-react";

const CHATBOT_API_URL =
  process.env.NODE_ENV === "development"
    ? "/api/social_media_chatbot"
    : "https://smchatbots.steamminds.org/api/social_media_chatbot";

const getAssistantReply = (data) => {
  if (typeof data === "string" && data.trim()) return data.trim();
  if (!data || typeof data !== "object") return null;

  if (typeof data.error === "string" && data.error.trim()) {
    return data.error.trim();
  }

  const candidates = [
    data.response,
    data.answer,
    data.message,
    data.result,
    data.reply,
    data.output,
    data.data?.response,
    data.data?.answer,
    data.data?.message,
  ];

  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }

  return null;
};

const WELCOME_MESSAGE = {
  id: 1,
  role: "assistant",
  text: "Hello! I'm your STEAM Minds AI Assistant. Ask me about courses, kits, VR labs, or how to get started — by typing or recording your voice.",
  isWelcome: true,
};

/** Must match backend MAX_PROMPT_LENGTH */
const MAX_PROMPT_LENGTH = 150;
const WORD_REVEAL_MS = 55;

const TypingDots = () => (
  <div className="flex items-center gap-1.5 py-0.5" aria-label="Assistant is typing">
    <span className="h-2 w-2 rounded-full bg-[#9CA3AF] animate-[typing-dot_0.9s_ease-in-out_infinite]" />
    <span className="h-2 w-2 rounded-full bg-[#9CA3AF] animate-[typing-dot_0.9s_ease-in-out_infinite] [animation-delay:0.15s]" />
    <span className="h-2 w-2 rounded-full bg-[#9CA3AF] animate-[typing-dot_0.9s_ease-in-out_infinite] [animation-delay:0.3s]" />
  </div>
);

const AIAssistantChatModal = ({ isOpen, onClose }) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const streamTimerRef = useRef(null);
  const isBusy = isSending || isStreaming;

  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen, isSending, isStreaming]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(
    () => () => {
      if (streamTimerRef.current) {
        clearInterval(streamTimerRef.current);
      }
    },
    [],
  );

  const revealAssistantMessage = (fullText, isError = false) => {
    if (streamTimerRef.current) {
      clearInterval(streamTimerRef.current);
      streamTimerRef.current = null;
    }

    const id = Date.now() + 1;
    const words = fullText.trim().split(/\s+/).filter(Boolean);

    if (isError || words.length <= 1) {
      setMessages((prev) => [
        ...prev,
        { id, role: "assistant", text: fullText, isError },
      ]);
      setIsSending(false);
      setIsStreaming(false);
      return;
    }

    setIsSending(false);
    setIsStreaming(true);
    setMessages((prev) => [
      ...prev,
      {
        id,
        role: "assistant",
        text: words[0],
        isError,
        isStreaming: true,
      },
    ]);

    let index = 1;
    streamTimerRef.current = setInterval(() => {
      index += 1;
      const nextText = words.slice(0, index).join(" ");
      const done = index >= words.length;

      setMessages((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, text: nextText, isStreaming: !done }
            : item,
        ),
      );

      if (done) {
        clearInterval(streamTimerRef.current);
        streamTimerRef.current = null;
        setIsStreaming(false);
      }
    }, WORD_REVEAL_MS);
  };

  if (!isOpen) return null;

  const handleSend = async (event) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || isBusy) return;

    if (trimmed.length > MAX_PROMPT_LENGTH) {
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), role: "user", text: trimmed },
      ]);
      setMessage("");
      revealAssistantMessage(
        `Message is too long. Please keep it under ${MAX_PROMPT_LENGTH} characters.`,
        true,
      );
      return;
    }

    const userMessage = { id: Date.now(), role: "user", text: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setIsSending(true);

    try {
      // credentials: "include" keeps session cookies so backend chat_history works.
      const response = await fetch(CHATBOT_API_URL, {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ query: trimmed }),
      });

      const raw = await response.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = raw
          ? { error: raw.replace(/\s+/g, " ").trim().slice(0, 180) }
          : null;
      }

      let reply;
      if (response.status === 429) {
        reply =
          getAssistantReply(data) ||
          "You're sending messages too fast. Please wait a minute and try again.";
      } else if (response.status === 400) {
        reply =
          getAssistantReply(data) ||
          "Invalid request. Please rephrase and try again.";
      } else if (response.status === 503) {
        reply =
          getAssistantReply(data) ||
          "RAG System is not available currently, please try again later.";
      } else if (!response.ok) {
        reply =
          getAssistantReply(data) ||
          `Sorry, something went wrong (${response.status}). Please try again later.`;
      } else {
        reply =
          getAssistantReply(data) ||
          "I couldn't find an answer right now. Please try again.";
      }

      revealAssistantMessage(reply, !response.ok);
    } catch (error) {
      console.error("AI Assistant request failed:", error);
      revealAssistantMessage(
        "Unable to reach the AI assistant. Please check your connection and try again.",
        true,
      );
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-end p-3 sm:p-5 lg:p-8">
      <style>{`
        @keyframes typing-dot {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.45; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>
      <button
        type="button"
        aria-label="Close AI Assistant overlay"
        className="absolute inset-0 bg-black/20"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="AI Assistant"
        className="relative z-10 flex h-[min(500px,78vh)] w-full max-w-[384px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_12px_40px_rgba(0,0,0,0.18)]"
      >
        <div className="flex items-center justify-between bg-[#FB7B02] p-4 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FFFFFF33]">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.64023 3.04C9.58743 3.14667 10.4578 3.48 11.2514 4.04C12.0194 4.59111 12.6253 5.29778 13.069 6.16C13.5298 7.04 13.7602 7.98667 13.7602 9V15H2.24023V9C2.24023 7.98667 2.47063 7.04 2.93143 6.16C3.37517 5.29778 3.98103 4.59111 4.74903 4.04C5.54263 3.48 6.41303 3.14667 7.36023 3.04V1H8.64023V3.04ZM12.4802 13.6667V9C12.4802 8.15556 12.2754 7.36889 11.8658 6.64C11.4733 5.93778 10.94 5.38222 10.2658 4.97333C9.5661 4.54667 8.8109 4.33333 8.00023 4.33333C7.18957 4.33333 6.43437 4.54667 5.73463 4.97333C5.0605 5.38222 4.52717 5.93778 4.13463 6.64C3.72503 7.36889 3.52023 8.15556 3.52023 9V13.6667H12.4802ZM8.00023 12.3333C7.41997 12.3333 6.8845 12.1844 6.39383 11.8867C5.90317 11.5889 5.5149 11.1844 5.22903 10.6733C4.94317 10.1622 4.80023 9.60444 4.80023 9C4.80023 8.39556 4.94317 7.83778 5.22903 7.32667C5.5149 6.81556 5.90317 6.41111 6.39383 6.11333C6.8845 5.81556 7.41997 5.66667 8.00023 5.66667C8.5805 5.66667 9.11597 5.81556 9.60663 6.11333C10.0973 6.41111 10.4856 6.81556 10.7714 7.32667C11.0573 7.83778 11.2002 8.39556 11.2002 9C11.2002 9.60444 11.0573 10.1622 10.7714 10.6733C10.4856 11.1844 10.0973 11.5889 9.60663 11.8867C9.11597 12.1844 8.5805 12.3333 8.00023 12.3333ZM8.00023 11C8.3501 11 8.67223 10.9111 8.96663 10.7333C9.26103 10.5556 9.49357 10.3133 9.66423 10.0067C9.8349 9.7 9.92023 9.36444 9.92023 9C9.92023 8.63556 9.8349 8.3 9.66423 7.99333C9.49357 7.68667 9.26103 7.44444 8.96663 7.26667C8.67223 7.08889 8.3501 7 8.00023 7C7.65037 7 7.32823 7.08889 7.03383 7.26667C6.73943 7.44444 6.5069 7.68667 6.33623 7.99333C6.16557 8.3 6.08023 8.63556 6.08023 9C6.08023 9.36444 6.16557 9.7 6.33623 10.0067C6.5069 10.3133 6.73943 10.5556 7.03383 10.7333C7.32823 10.9111 7.65037 11 8.00023 11ZM8.00023 9.66667C7.82103 9.66667 7.66957 9.60222 7.54583 9.47333C7.4221 9.34444 7.36023 9.18667 7.36023 9C7.36023 8.81333 7.4221 8.65556 7.54583 8.52667C7.66957 8.39778 7.82103 8.33333 8.00023 8.33333C8.17943 8.33333 8.3309 8.39778 8.45463 8.52667C8.57837 8.65556 8.64023 8.81333 8.64023 9C8.64023 9.18667 8.57837 9.34444 8.45463 9.47333C8.3309 9.60222 8.17943 9.66667 8.00023 9.66667Z"
                  fill="white"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold">AI Assistant</p>
              <p className="text-xs">{isBusy ? "Typing..." : "Online"}</p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close chat"
            onClick={onClose}
            className="rounded-full p-1 transition-opacity hover:opacity-80"
          >
            <X size={20} strokeWidth={2.2} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto bg-white px-4 py-4">
          {messages.map((item) => (
            <div
              key={item.id}
              className={`flex ${
                item.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-[22.5px] ${
                  item.role === "user"
                    ? "bg-[#FB7B02] text-white"
                    : item.isError
                      ? "bg-[#FEE2E2] text-[#991B1B]"
                      : "bg-[#F3F4F6] text-[#1F2937]"
                }`}
              >
                {item.text}
                {item.isStreaming ? (
                  <span className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse bg-current align-middle opacity-70" />
                ) : null}
              </div>
            </div>
          ))}

          {isSending ? (
            <div className="flex justify-start">
              <div className="rounded-2xl bg-[#F3F4F6] px-4 py-3">
                <TypingDots />
              </div>
            </div>
          ) : null}

          <div ref={messagesEndRef} />
        </div>

        <form
          onSubmit={handleSend}
          className="flex items-center gap-2 border-t border-[#E5E7EB] bg-white p-4"
        >
          <div className="relative flex min-w-0 flex-1 items-center">
            <input
              ref={inputRef}
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your question here..."
              maxLength={MAX_PROMPT_LENGTH}
              disabled={isBusy}
              className="h-11 w-full rounded-full border border-[#D1D5DB] bg-white py-2 pl-4 pr-11 text-sm text-[#111827] placeholder:text-[#9CA3AF] outline-none disabled:opacity-70"
            />
            <button
              type="button"
              aria-label="Record voice"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] transition-colors hover:text-[#6B7280]"
            >
              <Mic size={18} />
            </button>
          </div>
          <button
            type="submit"
            aria-label="Send message"
            disabled={!message.trim() || isBusy}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#D1D5DB] text-white transition-colors enabled:bg-[#FB7B02] disabled:cursor-not-allowed"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.4511 1.73322L10.9567 14.4532C10.9055 14.631 10.8351 14.7243 10.7455 14.7332C10.6559 14.7421 10.5727 14.6621 10.4959 14.4932L7.57746 8.42655L1.76626 5.99989C1.60413 5.93766 1.52519 5.86211 1.52946 5.77322C1.53373 5.68433 1.62119 5.60878 1.79186 5.54655L14.0031 1.30655C14.1737 1.24433 14.3017 1.25544 14.3871 1.33989C14.4724 1.42433 14.4937 1.55544 14.4511 1.73322ZM12.7231 3.15989L4.90226 5.86655L8.51186 7.37322L10.4575 11.4266L12.7231 3.15989Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
};

export default AIAssistantChatModal;
